// @section 1 Clients · ClientDetail — per-client workspace.
//
// Module-card grid mirroring the Acquisitions detail pattern. Each card is
// one operational surface for the client; cards stay independent so they can
// load and fail in isolation.
//
//   header        — client name, owner, status, last activity
//   modules grid  — Engagement Scope | Workstreams | Deliverables | Reviews
//
// All cards start empty/skeleton; data wiring lands after hq_delivery data
// cleanup (see related task).

import { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { listDeliverables } from '../../api';

// @subsection 1.1 Component

export default function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const [deliverables, setDeliverables] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    listDeliverables(clientId)
      .then((r: any) => !cancelled && setDeliverables(Array.isArray(r) ? r : []))
      .catch((e) => !cancelled && setErr(String(e)));
    return () => { cancelled = true; };
  }, [clientId]);

  return (
    <div className="proto-detail-body">
      {/* @block 1.2 Header */}
      <div className="mb-5">
        <div className="crumb text-[11px] mb-2">
          <NavLink to="/clients" className="text-muted hover:text-paper-100">Atlas</NavLink>
          <span className="sep mx-1.5 opacity-40">›</span>
          <NavLink to="/clients" className="text-muted hover:text-paper-100">Clients</NavLink>
          <span className="sep mx-1.5 opacity-40">›</span>
          <span className="text-paper-100 font-semibold">{clientId}</span>
        </div>
        <h1 className="text-2xl font-bold text-paper-100">Client workspace</h1>
        <p className="text-sm text-muted mt-1">
          Engagement scope, active workstreams, deliverables in flight, upcoming reviews. Each card pulls from
          its own slice of <code className="text-paper-100/70">hq_delivery</code> so one bad row doesn't take the page down.
        </p>
      </div>

      {/* @block 1.3 Module grid */}
      <div className="proto-detail-grid">

        {/* @block 1.4 Engagement Scope */}
        <div className="proto-module">
          <header>
            <h3>Engagement Scope</h3>
            <span className="mod-tag"><code>hq_delivery.engagements</code></span>
          </header>
          <div className="proto-module-body">
            <ScopePlaceholder />
          </div>
        </div>

        {/* @block 1.5 Workstreams */}
        <div className="proto-module">
          <header>
            <h3>Workstreams</h3>
            <span className="mod-tag"><code>hq_delivery.workstreams</code></span>
          </header>
          <div className="proto-module-body">
            <EmptyHint text="Workstreams will list here once they're modeled. After the data cleanup pass." />
          </div>
        </div>

        {/* @block 1.6 Deliverables — wired (uses listDeliverables) */}
        <div className="proto-module span-2">
          <header>
            <h3>Deliverables</h3>
            <span className="mod-tag"><code>hq_delivery.deliverables</code></span>
          </header>
          <div className="proto-module-body">
            {deliverables === null && !err && <EmptyHint text="Loading…" />}
            {err && <EmptyHint text={`Failed: ${err}`} />}
            {deliverables && deliverables.length === 0 && !err && (
              <EmptyHint text="No deliverables for this client yet." />
            )}
            {deliverables && deliverables.length > 0 && (
              <table className="proto-table">
                <thead>
                  <tr><th>Title</th><th>Status</th><th>Updated</th></tr>
                </thead>
                <tbody>
                  {deliverables.map((d, i) => (
                    <tr key={d.id || i}>
                      <td>{d.title || d.name || '(untitled)'}</td>
                      <td className="muted">{d.status || '—'}</td>
                      <td className="muted">{d.updated_at ? new Date(d.updated_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* @block 1.7 Reviews */}
        <div className="proto-module">
          <header>
            <h3>Reviews</h3>
            <span className="mod-tag"><code>hq_delivery.reviews</code></span>
          </header>
          <div className="proto-module-body">
            <EmptyHint text="QBRs + check-ins will surface here." />
          </div>
        </div>

      </div>
    </div>
  );
}

// @subsection 1.8 Internal helpers

function ScopePlaceholder() {
  return (
    <dl className="proto-dl">
      <dt>Status</dt><dd className="muted">—</dd>
      <dt>Owner</dt><dd className="muted">—</dd>
      <dt>Started</dt><dd className="muted">—</dd>
      <dt>Scope</dt><dd className="muted">Will hydrate from the engagement record after data cleanup.</dd>
    </dl>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <div className="text-[12px] text-muted italic">{text}</div>;
}
